import React, { useCallback, useEffect } from 'react';
import { useStore } from 'react-redux';
import { useTrackableLink, useVisibilityObserver } from '@noths/polaris-client-react-hooks';
import {
  FavouriteButton,
  IconArrowForward,
  IconLocalShippingLocal,
  Price,
} from '@noths/polaris-client-ribbons-design-system';
import { visuallyHidden } from '@noths/polaris-client-styles';
import { getProductImageURL } from '@noths/polaris-client-utils';
import { CurrencyCode, Product } from '@noths/polaris-dev-ts-types';
import Image from 'next/image';

import * as copy from '../constants/copy';
import { FEATURED_PRODUCT_TESTID } from '../constants/elementIds';
import { getFeaturedProductClickImpression } from '../tracking/utils';
import * as styles from '../styles/FeaturedProduct.styles';

interface FeaturedProductProps {
  currency: CurrencyCode;
  isFavorite: boolean;
  product: Product | null;
  visible: boolean;
  onComponentVisible: VoidFunction;
  onProductOutOfStock: VoidFunction;
  onUpdateFavorite: (productCode: number) => void;
}

export const FeaturedProduct = ({
  currency,
  isFavorite,
  product,
  visible,
  onComponentVisible,
  onProductOutOfStock,
  onUpdateFavorite,
}: FeaturedProductProps) => {
  const { getState } = useStore();
  const state = getState();

  const { trackLinkClick } = useTrackableLink();
  const { observedElementRef: containerRef } = useVisibilityObserver(() => onComponentVisible());

  const onProductLinkClick = useCallback(
    trackLinkClick(getFeaturedProductClickImpression(state)!),
    [state],
  );

  useEffect(() => {
    if (product?.inventoryStatus === 'out_of_stock') {
      onProductOutOfStock();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!product || !visible) {
    return null;
  }

  const { images, title, prices, preSalePrices, onSale, links, freeDomesticDelivery } = product;

  const price = prices?.find((prices) => prices.currency === currency);
  const preSalePrice = preSalePrices?.find((price) => price.currency === currency);
  const shouldDisplaySalePrice = Boolean(onSale && preSalePrice);

  return (
    <div
      ref={containerRef}
      css={styles.featuredProductContainer}
      data-testid={FEATURED_PRODUCT_TESTID}
    >
      <a css={styles.productAnchorWrapper} href={links[0]?.href ?? ''} onClick={onProductLinkClick}>
        <div css={styles.featuredProduct}>
          <div css={styles.productImageContainer}>
            <div css={styles.productImage}>
              <Image
                src={getProductImageURL(images[0].href, 'preview')}
                alt=""
                layout="fill"
                objectFit="cover"
                // This adds preload tag to head as this image is above the fold and becomes the new LCP when on the page (according to Lighthouse)
                priority
                unoptimized
              />
            </div>
          </div>
          <div css={styles.productInfo}>
            <div css={styles.productTitle}>{title}</div>
            <div css={styles.productPriceWrapper} aria-hidden="true">
              {price && (
                <Price css={styles.price} isSalePrice={shouldDisplaySalePrice} {...price} />
              )}
              {shouldDisplaySalePrice && (
                <Price isPreviousPrice css={styles.salePrice} {...preSalePrice!} />
              )}
            </div>
            {freeDomesticDelivery && (
              <span css={styles.freeDelivery} aria-hidden="true">
                <IconLocalShippingLocal
                  height={16}
                  width={16}
                  color={`var(--neutral-black-base)`}
                />
                {copy.FREE_UK_DELIVERY}
              </span>
            )}
            <div css={styles.productLink}>
              <span css={visuallyHidden}>{`, `}</span>
              {copy.PRODUCT_LINK}
              <IconArrowForward
                css={styles.productLinkIcon}
                width={16}
                height={16}
                color={`var(--brand-purple-base)`}
              />
            </div>
          </div>
        </div>
      </a>
      <div css={styles.favouriteButtonWrapper}>
        <FavouriteButton
          css={styles.favouriteButton}
          onClick={() => onUpdateFavorite(product.code)}
          active={isFavorite}
          size="large"
        />
      </div>
    </div>
  );
};
